import ReactDOM from 'react-dom'
import { useCallback, useState } from 'react'
import { useFetcher } from '@remix-run/react'
import { ModalContent } from './components/ModalContent'
import useCookie from '~/hooks/useCookie'
import { trackMarketingEventFn } from '~/utils/tracking/trackMarketingEvent'
import { LockIcon } from '../icons'
import { Button } from '../general/Button'
import { trackEvent } from '~/utils/tracking/trackEvent'

export const CookieBanner = () => {
  const fetcher = useFetcher()
  const { handlePreferenceSave, isPreferenceSaved } = useCookie()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [cookieConsent, setCookieConsent] = useState(['EssentialCookies'])

  const handleAccept = useCallback(() => {
    fetcher.submit(
      { analyticalCookies: '1', marketingAndTrackingCookies: '1' },
      { method: 'post', action: '/' },
    )

    const consentValue = {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
    }

    trackMarketingEventFn('cookie_choice', 'Cookie Choice', `Essential, Analytical, Marketing`)
    ;(window as Window).gtag('consent', 'update', consentValue)

    // Track Accept All event
    trackEvent('click_cookie_banner', {
      website_host: 'career.io',
      label: 'click_cookie_banner',
      cta_text: 'Accept All',
      cookie_settings: 'Accept all',
    })

    handlePreferenceSave(true)
    setShowModal(false)
  }, [fetcher, handlePreferenceSave])

  const handleToggle = useCallback(
    (cookieType: string) => {
      if (cookieType === 'analytical') {
        if (cookieConsent.includes('AnalyticalCookies')) {
          setCookieConsent(cookieConsent.filter((value: string) => value !== 'AnalyticalCookies'))
        } else {
          setCookieConsent((pre: Array<string>) => [...pre, 'AnalyticalCookies'])
        }
      } else if (cookieType === 'targeting') {
        if (cookieConsent.includes('TargetingCookies')) {
          setCookieConsent(cookieConsent.filter((value: string) => value !== 'TargetingCookies'))
        } else {
          setCookieConsent((pre: Array<string>) => [...pre, 'TargetingCookies'])
        }
      }
    },
    [cookieConsent],
  )

  const handleSavePreferences = useCallback(() => {
    fetcher.submit(
      {
        analyticalCookies: cookieConsent.includes('AnalyticalCookies') ? '1' : '0',
        marketingAndTrackingCookies: cookieConsent.includes('TargetingCookies') ? '1' : '0',
      },
      {
        method: 'post',
        action: '/',
      },
    )
    setShowModal(false)
    handlePreferenceSave(true)

    const analytical = cookieConsent.includes('AnalyticalCookies') ? ', Analytical,' : ''
    const marketing = cookieConsent.includes('TargetingCookies') ? 'Marketing,' : ''
    const consentValue = {
      consent: {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: cookieConsent.includes('TargetingCookies') ? 'granted' : 'denied',
        analytics_storage: cookieConsent.includes('AnalyticalCookies') ? 'granted' : 'denied',
      },
    }

    if (marketing === 'Marketing,') {
      consentValue.consent.ad_personalization = 'granted'
    }
    if (analytical === ', Analytical,') {
      consentValue.consent.analytics_storage = 'granted'
    }

    let cookieSettings = 'Essential only'

    if (marketing === 'Marketing,' && analytical === ', Analytical,') {
      cookieSettings = 'Accept all'
    } else if (marketing === 'Marketing,') {
      cookieSettings = 'Marketing cookies'
    } else if (analytical === ', Analytical,') {
      cookieSettings = 'Analytical cookies'
    }

    trackMarketingEventFn(
      'cookie_choice',
      'Cookie Choice',
      `Essential${analytical}${marketing}${JSON.stringify(consentValue)}`.trim(),
    )
    ;(window as Window).gtag('consent', 'update', consentValue)

    trackEvent('click_cookie_banner', {
      website_host: 'career.io',
      label: 'click_cookie_banner',
      cta_text: 'Save Preferences',
      cookie_settings: cookieSettings,
    })
  }, [cookieConsent, fetcher, handlePreferenceSave])

  return (
    <>
      {!showModal && !isPreferenceSaved && (
        <div className="max-md:w-auto max-lg:max-w-[936px] my-0 mx-auto max-w-[1120px]">
          <div className="font-tt_talent flex flex-col items-start py-6 px-8 gap-4 fixed z-500 max-w-[1120px] min-h-[248px] bottom-5 rounded-[24px] shadow-[0px_0px_1px_rgba(15,56,113,0.32),0px_2px_4px_-1px_rgba(15,56,113,0.08),0px_8px_20px_-4px_rgba(15,56,113,0.12)] bg-white max-xl:right-4 max-xl:left-4 max-lg:max-w-[936px] max-lg:min-h-[232px] max-lg:right-5 max-lg:max-left-5 max-md:w-auto max-md:h-auto max-md:ml-0 max-md:left-2 max-md:right-2 max-md:bottom-2 max-md:p-5">
            <div className="flex flex-col items-start p-0 gap-2 h-auto flex-none order-0 self-stretch flex-grow-0">
              <div className="flex items-center w-full gap-2 max-lg:gap-1">
                <p className="text-s text-neutral-100">Protect your data</p>
                <LockIcon className="max-md:h-[18px] max-md:max-w-[18px] max-lg:h-6 max-lg:max-w-6" />
              </div>
              <div className="h-24 w-auto text-neutral-100 flex-none flex-grow-0 max-lg:h-auto self-stretch text-xs font-normal">
                We use cookies to provide our services, improve the user experience, for analysis
                and marketing purposes. By giving your consent, you also agree that your data may be
                transferred to the USA by the use of cookies. You can revoke your consent at any
                time.You can find further information in our{' '}
                <a
                  href="https://career.io/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-50"
                  onClick={() => {
                    trackEvent('click_cookie_banner', {
                      website_host: 'career.io',
                      label: 'click_cookie_banner',
                      cta_text: 'privacy policy',
                      url: 'https://career.io/privacy',
                    })
                  }}
                >
                  privacy policy
                </a>{' '}
                and{' '}
                <a
                  href="https://career.io/cookie-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-50"
                  onClick={() => {
                    trackEvent('click_cookie_banner', {
                      website_host: 'career.io',
                      label: 'click_cookie_banner',
                      cta_text: 'cookie policy',
                      url: 'https://career.io/cookie-policy',
                    })
                  }}
                >
                  cookie policy
                </a>
                .
              </div>
            </div>
            <div className="flex w-auto gap-3 max-lg:gap-2">
              <Button text="Accept All" color="blue" onClick={handleAccept} />
              <Button
                color="transparent"
                text="Cookie Preferences"
                onClick={() => {
                  setShowModal((pre: boolean) => !pre)
                  trackEvent('open_cookie_banner_settings', {
                    website_host: 'career.io',
                    label: 'open_cookie_banner_settings',
                    cta_text: 'Cookie Preferences',
                  })
                }}
                className="border-neutral-15 hover:text-neutral-100 text-neutral-100"
              />
            </div>
          </div>
        </div>
      )}
      {showModal &&
        typeof document !== 'undefined' &&
        ReactDOM.createPortal(
          <ModalContent
            closeModal={setShowModal}
            handleAccept={handleAccept}
            CookieConsent={cookieConsent}
            handleToggle={handleToggle}
            handleSavePreferences={handleSavePreferences}
          />,
          window.document.body,
        )}
    </>
  )
}
